<template>
  <div class="aviation-calculator">
    <div class="calculator-header">
      <h3>
        <i class="bx bxs-plane-take-off"></i>
        Калькулятор авиаперевозок
      </h3>
    </div>
    
    <div class="calculator-content">
      <!-- Форма ввода параметров -->
      <div class="calculator-form">
        <div class="form-row">
          <div class="form-group">
            <label>Назначение</label>
            <input 
              type="text" 
              v-model="formData.destination" 
              disabled
              placeholder="Например: MOW"
            >
          </div>
          <div class="form-group exw-group">
            <label>EXW</label>
            <div class="exw-input-container">
              <input 
                type="text" 
                v-model="formData.origin" 
                placeholder="Например: ShenZhen"
                class="exw-input"
              >
              <select 
                v-model="selectedCity" 
                @change="onCitySelect"
                class="exw-select"
              >
                <option value="">Выберите город</option>
                <option 
                  v-for="city in availableCities" 
                  :key="city"
                  :value="city"
                >
                  {{ city }}
                </option>
                <option value="other">Другое</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>Вес, кг</label>
            <input 
              type="number" 
              v-model="formData.weight" 
              placeholder="Введите вес"
              @input="calculate"
            >
          </div>
          <div class="form-group">
            <label>Объем, м³</label>
            <input 
              type="number" 
              v-model="formData.volume" 
              placeholder="Введите объем"
              @input="calculate"
            >
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>Тариф</label>
            <select v-model="formData.selectedTariff" @change="calculate">
              <option 
                v-for="tariff in tariffs" 
                :key="tariff.tariff_code"
                :value="tariff"
              >
                {{ tariff.name }} ({{ tariff.total_delivery_days }} дней)
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>Условия поставки</label>
            <select v-model="formData.terms" disabled>
              <option value="EXW">EXW</option>
              <!-- <option value="FOB">FOB</option>
              <option value="CIF">CIF</option> -->
            </select>
          </div>
        </div>
      </div>
      
      <!-- Информация о тарифах -->
      <div class="tariffs-info" v-if="tariffs.length > 0">
        <h4>Доступные тарифы</h4>
        <div class="tariffs-grid">
          <div 
            v-for="tariff in tariffs" 
            :key="tariff.tariff_code"
            class="tariff-card"
            :class="{ 
              active: formData.selectedTariff && formData.selectedTariff.tariff_code === tariff.tariff_code,
              calculated: getTariffCalculation(tariff)
            }"
            @click="selectTariff(tariff)"
          >
            <div class="tariff-header">
              <h5>{{ tariff.name }}</h5>
              <span class="tariff-code">{{ tariff.tariff_code }}</span>
            </div>
            <div class="tariff-price" v-if="getTariffCalculation(tariff)">
              <span class="price-usd">${{ getTariffCalculation(tariff).totalCostUsd.toFixed(2) }}</span>
            </div>
            <div class="tariff-price" v-else>
              <span class="price-placeholder">Введите данные для расчета</span>
            </div>
            <div class="tariff-details">
              <div class="detail">
                <span class="label">Срок доставки:</span>
                <span class="value">{{ tariff.total_delivery_days }}</span>
              </div>
              <div class="detail">
                <span class="label">Рейсов в неделю:</span>
                <span class="value">{{ tariff.flights_per_week }}</span>
              </div>
              <div class="detail">
                <span class="label">До аэропорта:</span>
                <span class="value">{{ tariff.delivery_days_to_airport }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Результаты расчета -->
      <div class="calculation-results" v-if="calculationResult && formData.selectedTariff">
        <div class="results-header">
          <h4>Детали выбранного тарифа</h4>
        </div>
        
        <div class="results-content">
          <div class="result-item">
            <span class="label">Название тарифа:</span>
            <span class="value">{{ formData.selectedTariff.name }}</span>
          </div>
          <div class="result-item">
            <span class="label">Срок доставки до Аэропорта Вылета:</span>
            <span class="value">{{ formData.selectedTariff.delivery_days_to_airport }} дней</span>
          </div>
          <div class="result-item">
            <span class="label">Срок Экспортного ТО:</span>
            <span class="value">{{ formData.selectedTariff.export_customs_days }} дней</span>
          </div>
          <div class="result-item">
            <span class="label">Количество вылетов в неделю:</span>
            <span class="value">{{ formData.selectedTariff.flights_per_week }}</span>
          </div>
          <div class="result-item">
            <span class="label">Дней доставки от склада в Китае до SVO, итого:</span>
            <span class="value">{{ formData.selectedTariff.total_delivery_days }} дней</span>
          </div>
          <div class="result-item total">
            <span class="label">Общая стоимость (USD):</span>
            <span class="value">${{ calculationResult.totalCost.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_CALCULATOR_SETTINGS_AVIATION } from '@/constants'

export default {
  name: 'AviationCalculator',
  
  data() {
    return {
      isLoading: false,
      tariffs: [],
      tariffCalculations: [],
      selectedCity: '',
      availableCities: [
        'Шанхай / Shanghai',
        'Гуанжоу / Guangzhou', 
        'Шеньжень / Shenzhen',
        'Шаньтоу / Shantou',
        'Чжуншань / Zhongshan',
        'Фошань / Foshan',
        'Хуэйчжоу / Huizhou',
        'Нингбо / Ningbo',
        'Тяньцзинь / Tianjin',
        'Циндао / Qingdao',
        'Си ань / Xi\'an',
        'Чэнду / Chengdu',
        'Чунцин / Chongqing',
        'Сямынь / Xiamen'
      ],
      formData: {
        weight: '',
        volume: '',
        origin: '',
        destination: 'MOW',
        selectedTariff: null,
        terms: 'EXW'
      },
      calculationResult: null
    }
  },
  
  async mounted() {
    await this.loadTariffs()
  },
  
  methods: {
    async loadTariffs() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_AVIATION)
        
        if (response.data.success && response.data.data.tariffs) {
          this.tariffs = response.data.data.tariffs
          // Выбираем первый тариф по умолчанию
          if (this.tariffs.length > 0) {
            this.formData.selectedTariff = this.tariffs[0]
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки тарифов:', error)
        this.$toast.error('Ошибка загрузки тарифов')
      } finally {
        this.isLoading = false
      }
    },
    
    selectTariff(tariff) {
      this.formData.selectedTariff = tariff
      this.calculate()
    },
    
    onCitySelect() {
      if (this.selectedCity === 'other') {
        this.formData.origin = ''
        this.selectedCity = ''
      } else if (this.selectedCity) {
        this.formData.origin = this.selectedCity
      }
    },
    
    getTariffCalculation(tariff) {
      return this.tariffCalculations.find(calc => calc.tariff.tariff_code === tariff.tariff_code)
    },
    
    async calculate() {
      if (!this.formData.weight || !this.formData.volume || this.tariffs.length === 0) {
        this.calculationResult = null
        this.tariffCalculations = []
        return
      }
      
      const grossWeight = parseFloat(this.formData.weight)  // B13 - фактический вес
      const volume = parseFloat(this.formData.volume)        // B14 - объем
      
      // Формула из Excel: =ЕСЛИ(B14*167<B13;B13;B14*167)
      // Определяем расчетный вес: если объем*167 < фактический вес, то используем фактический вес, иначе объем*167
      const calculatedWeight = Math.max(grossWeight, volume * 167)
      
      // Проверяем альтернативные варианты расчета веса
      const volumeWeight = volume * 167
      console.log('=== Расчет веса ===')
      console.log(`Фактический вес: ${grossWeight} кг, Объем*167: ${volumeWeight} кг`)
      console.log(`Расчетный вес: ${calculatedWeight} кг`)
      
      // Получаем курс USD
      const usdRate = await this.getUsdRate()
      
      // Рассчитываем все тарифы
      this.tariffCalculations = []
      
      for (const tariff of this.tariffs) {
        const pricing = tariff.pricing
        
        // Формула из Excel: =ЕСЛИ(I3<100;I3*J3;ЕСЛИ(I3>300;I3*L3;I3*K3))
        // I3 - расчетный вес, J3 - тариф до 100кг, K3 - тариф 100-300кг, L3 - тариф свыше 300кг
        let transportCost = 0
        if (calculatedWeight < 100) {
          transportCost = calculatedWeight * pricing.weight_45  // J3 - тариф до 100кг
        } else if (calculatedWeight > 300) {
          transportCost = calculatedWeight * pricing.weight_300  // L3 - тариф свыше 300кг
        } else {
          transportCost = calculatedWeight * pricing.weight_100  // K3 - тариф 100-300кг
        }
        
        // Pickup: ЕСЛИ((M3*I3)<N3;N3;(M3*I3))
        const pickupCost = Math.max(
          calculatedWeight * pricing.pickup_per_kg,  // M3*I3
          pricing.pickup_min_fix          // N3
        )
        
        // Terminal: ЕСЛИ((P3*I3)<Q3;Q3;(P3*I3))
        const terminalCost = Math.max(
          calculatedWeight * pricing.per_kg_th,      // P3*I3
          pricing.fix_min_th             // Q3
        )
        
        // Общая стоимость в юанях по формуле Excel:
        // =ЕСЛИ(I3<100;I3*J3;ЕСЛИ(I3>300;I3*L3;I3*K3))++(ЕСЛИ((M3*I3)<N3;N3;(M3*I3)))+O3+(ЕСЛИ((P3*I3)<Q3;Q3;(P3*I3)))+R3+S3
        const totalCostYuan = transportCost + pickupCost + pricing.fix_exw_cc + terminalCost + pricing.fix_awb + pricing.fix_base
        
        // Конвертация в доллары
        const totalCostUsd = totalCostYuan / usdRate
        
        // Логируем для разработчиков (внутренние расчеты)
        console.log(`Тариф ${tariff.name}: ${totalCostYuan.toFixed(2)} юаней = $${totalCostUsd.toFixed(2)}`)
        
        this.tariffCalculations.push({
          tariff,
          totalCostYuan,
          totalCostUsd,
          transportCost,
          pickupCost,
          terminalCost
        })
      }
      
      // Если выбран тариф, показываем детали
      if (this.formData.selectedTariff) {
        const selectedCalc = this.tariffCalculations.find(calc => 
          calc.tariff.tariff_code === this.formData.selectedTariff.tariff_code
        )
        
        if (selectedCalc) {
          this.calculationResult = {
            transportCost: selectedCalc.totalCostUsd,
            pickupCost: selectedCalc.pickupCost / usdRate,
            terminalCost: selectedCalc.terminalCost / usdRate,
            awbCost: selectedCalc.tariff.pricing.fix_awb / usdRate,
            totalCost: selectedCalc.totalCostUsd,
            yuanCost: selectedCalc.totalCostYuan,
            usdRate: usdRate
          }
        }
      }
      
      // Логируем итоговые результаты для разработчиков
      console.log('=== Итоги расчета ===')
      console.log(`Вес: ${grossWeight}кг, Объем: ${volume}м³ → Расчетный: ${calculatedWeight}кг`)
      console.log(`Курс USD/CNY: ${usdRate.toFixed(4)} (внутренний расчет)`)
    },
    
    async getUsdRate() {
      try {
        // Получаем курсы валют от ЦБР
        const response = await fetch('https://www.cbr-xml-daily.ru/daily_json.js')
        const data = await response.json()
        
        const usdToRub = data.Valute.USD.Value
        const cnyToRub = data.Valute.CNY.Value
        
        // Вычисляем курс USD к CNY: (USD/RUB) / (CNY/RUB) = USD/CNY
        const usdToCny = usdToRub / cnyToRub
        
        console.log('Курсы ЦБР:')
        console.log('USD/RUB:', usdToRub)
        console.log('CNY/RUB:', cnyToRub)
        console.log('USD/CNY:', usdToCny)
        
        return usdToCny
      } catch (error) {
        console.error('Ошибка получения курса USD:', error)
        return 7.11 // Fallback курс USD/CNY
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.aviation-calculator {
  .calculator-header {
    margin-bottom: 15px;
    
    h3 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #0d6348;
      font-size: 18px;
      
      i {
        font-size: 20px;
      }
    }
  }
  
  .calculator-content {
    .calculator-form {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
        margin-bottom: 15px;
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }
      }
      
      .form-group {
        display: flex;
        flex-direction: column;
        
        label {
          font-size: 12px;
          font-weight: 500;
          color: #333;
          margin-bottom: 5px;
        }
        
        input, select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.3s;
          
          &:focus {
            outline: none;
            border-color: #0d6348;
          }
        }
        
        &.exw-group {
          .exw-input-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0px;
            
            @media (max-width: 768px) {
              grid-template-columns: 1fr;
            }
          }
          
          .exw-input {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: none;
            
            &:focus {
              border-color: #0d6348;
            }
          }
          
          .exw-select {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            background: #f8f9fa;
            cursor: pointer;
            
            &:focus {
              border-color: #0d6348;
              background: #fff;
            }
          }
        }
      }
    }
    
    .calculation-results {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      border: 1px solid #e9ecef;
      
      .results-header {
        margin-bottom: 15px;
        
        h4 {
          margin: 0;
          color: #0d6348;
          font-size: 16px;
        }
      }
      
      .results-content {
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
          
          &.total {
            border-bottom: none;
            font-weight: 600;
            font-size: 16px;
            color: #0d6348;
            margin-top: 10px;
            padding-top: 15px;
            border-top: 2px solid #0d6348;
          }
          
          .label {
            color: #666;
            font-size: 14px;
          }
          
          .value {
            font-weight: 500;
            color: #333;
          }
        }
      }
      
      .delivery-info {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #e9ecef;
        
        .info-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #666;
          
          i {
            color: #0d6348;
          }
        }
      }
    }
    
    .tariffs-info {
      margin-bottom: 20px;
      
      h4 {
        margin-bottom: 15px;
        color: #0d6348;
        font-size: 16px;
      }
      
      .tariffs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 15px;
        
        .tariff-card {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            border-color: #0d6348;
            box-shadow: 0 4px 12px rgba(13, 99, 72, 0.1);
            transform: translateY(-2px);
          }
          
          &.active {
            border-color: #0d6348;
            background: #f0f8f5;
            box-shadow: 0 4px 12px rgba(13, 99, 72, 0.15);
          }
          
          &.calculated {
            .price-usd {
              color: #0d6348;
              font-weight: 700;
            }
          }
          
          .tariff-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            
            h5 {
              margin: 0;
              color: #0d6348;
              font-size: 16px;
              font-weight: 600;
            }
            
            .tariff-code {
              background: #0d6348;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: 500;
            }
          }
          
          .tariff-price {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 12px;
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;
            
            .price-usd {
              font-size: 18px;
              font-weight: 700;
              color: #0d6348;
            }
            
            .price-placeholder {
              font-size: 12px;
              color: #999;
              font-style: italic;
              text-align: center;
              width: 100%;
            }
          }
          
          .tariff-details {
            .detail {
              display: flex;
              justify-content: space-between;
              margin-bottom: 6px;
              font-size: 12px;
              
              .label {
                color: #666;
              }
              
              .value {
                font-weight: 500;
                color: #333;
              }
            }
          }
        }
      }
    }
  }
}
</style>
